<template>
    <admin-layout 
        title="Dependents"
        show-back
        :backUrl="`/accounts/patients`">
        <PatientLayout :patient-id="patientId">
            <template #contents>
                <div v-if="isNetworkAvailable">
                    <TabComponent
                        :tabs="tabs"
                        :button-items="true"
                    >
                    <template #buttons v-if="isNetworkAvailable">
                        <ButtonComponent
                            class="text-gray-900"
                            secondary
                            exportBtn
                            @click="exportData"
                            :disabled="loader"
                        >
                            Export
                        </ButtonComponent>
                    </template>
                    </TabComponent>
                </div>
                <!-- Filter -->
                <div class="px-6 py-3" v-if="isNetworkAvailable">
                    <FilterComponent
                        :search="searchText"
                        @update:searchText="(value) => (searchText = value)"
                        :sidebar-filters="false"
                        :custom-filters="activeTab !== 'activity_logs'"
                    >
                    </FilterComponent>
                </div>
                <div class="">
                    <DataTable 
                        :headers="headers" 
                        :count="items?.total"
                    >
                        <template #body >
                            <tr v-for="(item, key) in items?.data" :key="key">
                                <td class="px-6 py-3 text-sm text-gray-900 whitespace-nowrap">
                                    <!-- {{ item.dependent_id }} -->
                                    {{ item.patient_id }}
                                </td>
                                <td class="w-8/12 px-6 py-3 text-sm text-gray-900 whitespace-nowrap">
                                    {{ item?.first_name }} {{ item?.last_name }}
                                    <p class="text-neutral-500">{{ item?.email }}</p>
                                </td>
                                <td class="px-6 py-3 text-sm text-gray-900 whitespace-nowrap">
                                    {{ formatDate(item.birthdate) }}
                                </td>
                                <td class="px-6 py-3 text-sm text-gray-900 whitespace-nowrap">
                                    {{ formatDateTime(item.created_date) }}
                                </td>
                                <td class="text-center">
                                    <ActionButton 
                                        edit-btn
                                        @click="router.push(`/accounts/patients/show/${item.patient_id}?dependent=true`)"
                                    />
                                </td>
                            </tr>
                        </template>
                    </DataTable>
                    <page-pagination class="mb-6" 
                        :items="items"  
                    />
                </div>
            </template>
        </PatientLayout>
        <PageLoader v-if="loader" />
    </admin-layout>
</template>

<script setup lang="ts">
import AdminLayout from '@/layouts/AdminLayout.vue';
import PatientLayout from '../components/PatientLayout.vue';
import FilterComponent from '@/components/FilterComponent.vue';
import DataTable from '@/components/DataTable.vue';
import ActionButton from '@/components/ActionButton.vue';
import TabComponent from '@/components/TabComponent.vue';
import ButtonComponent from '@/components/ButtonComponent.vue';
import PageLoader from '@/components/PageLoader.vue';
import PagePagination from '@/components/PagePagination.vue';
import DatePicker from '@/components/inputs/DatePicker.vue';
import MultiselectInput from '@/components/inputs/MultiselectInput.vue';

import { useRouter, useRoute } from 'vue-router';
import { ref, computed, onMounted, watch } from 'vue';
import { onIonViewWillEnter } from '@ionic/vue';
import { format } from 'date-fns';
import { startOfYear, endOfYear } from 'date-fns';
import { useNetworkStore } from '@/store/network';
import { paginatePage } from '@/helpers/PageHelper';
import ReportService from '@/classes/ReportService';
import Patient from '@/classes/Patient';
import { formatDateTime, formatDate } from '@/helpers/TimeFormatter'

import throttle from "lodash/throttle";
import AuthService from "@/classes/AuthService";

interface activeMember {
    Full_Name: string,
    Hospital_Number: string,
    Last_Login_At: Date,
}

interface Item {
    data: activeMember[]
    total: number
}


const router = useRouter();
const route = useRoute();
const patientId = ref(route.params.id);

// Data
const loader    = ref(true);
const showError = ref(false);
const errorMsg  = ref(null);

const networkStore = useNetworkStore();
const isNetworkAvailable = computed(() => networkStore.isNetworkAvailable);

// Query
const searchText = ref();
const activeTab  = ref();
const goToPage   = ref();

// Page Renderer Data
const items       = ref(null);
const items_count = ref(0);
const query       = ref(null);

const tabs: { name: string, count: number, href: string, allowed: true }[] = [
    {
        name: 'Active',
        count: 0,
        href: '/accounts/dependents/bookings/' + patientId.value,
        allowed: true
    }
];

const headers: { text: string }[] = [
    { text: 'ID' },
    { text: 'Patient' },
    { text: 'Birthdate' },
    { text: 'Date Created'}
];

/**
 * ********************************
 * Methods 
 * ********************************
 */
 const indexDependents = (data: string) => {
    loader.value = true;

    if(isNetworkAvailable.value) {
        console.log("Online - Fetching Patient Dependents...")

        const pageNum = data?.match(/\d+/)[0] ? data?.match(/\d+/)[0] : 1;
        const queryString =  searchText.value ? `&query=${searchText.value}` : ''
        const additionalParam = `&page_number=${pageNum}&per_page=10&${queryString}&requested_at=${new Date().toISOString()}}`
    
        goToPage.value = data ? `page=${pageNum}` : 'page=1';
    
        Patient.fetchDependents(patientId.value, goToPage.value + additionalParam + `&patient_id=${patientId.value}`)
            .then((response: any) => {
                loader.value      = false;
                items.value       = response.data;
                items_count.value = response.data.total
                query.value       = response.data.query
            })
            .catch((err) => {
                loader.value    = false;
                showError.value = true;
                errorMsg.value  = err.response.data.message;
                
                if(err.response.status == 401) {
                    window.location.replace('/login');
                }
            })
    } else {
        console.log("Offline - Fetching Patient Dependents...")

        const pageNum = data?.match(/\d+/)[0] ? data?.match(/\d+/)[0] : 1;

        Patient.fetchDependents(patientId.value, `page=${pageNum}&page_number=${pageNum}&per_page=10&patient_id=${patientId.value}`)
        .then((response: any) => {
            loader.value      = false;
            items.value       = response.data;
            items_count.value = response.data.total
            query.value       = response.data.query
        })
        .catch(() => {
            loader.value    = false;
            showError.value = true;
            errorMsg.value = "Oppps... No available data was saved for offline use.";
        })
    }
}

watch(
    () => isNetworkAvailable.value,
    () => {
        indexDependents(goToPage.value);
    }
);

onMounted(() => {
    loader.value = true;
    setTimeout(() => {
        console.log('Mounted Hook - Network: ' + isNetworkAvailable.value);
        indexDependents(goToPage.value);
    }, 1000);
})

onIonViewWillEnter(() => {
    loader.value = true;
    setTimeout(() => {
        console.log('Mounted Hook - Network: ' + isNetworkAvailable.value);
        indexDependents(goToPage.value);
    }, 1000);
})
</script>